"use client";

import { FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from "react";
import { Content, Wrapper } from "./content-transition.styles";
import { ContentTransitionPage } from "./content-transition-page/content-transition-page";
import { usePreviousValue } from "../hooks/previous-value";

export type PageStatus = {
    pageKey: number;
    direction: "prev" | "next";
};

export interface ContentTransitionProps {
    /**
     * Status of active page.
     */
    pageStatus: PageStatus;

    /**
     * Class name of the content transition.
     */
    className?: string;
}

interface ContentTransitionComponent extends FunctionComponent<PropsWithChildren<ContentTransitionProps>> {
    Page: typeof ContentTransitionPage;
}

export const ContentTransition: ContentTransitionComponent = ({ pageStatus, className, children }) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const previousPagekey = usePreviousValue(pageStatus.pageKey) || 0;
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

    useEffect(() => {
        const allChildren = Array.from(contentRef.current?.children || []) as HTMLDivElement[];
        const currentChild = allChildren[previousPagekey];
        if (currentChild) {
            currentChild.style.display = "block";
        }
    }, []);

    useEffect(() => {
        if (isFirstRender) {
            setIsFirstRender(false);
            return;
        }

        if (contentRef.current) {
            const allChildren = Array.from(contentRef.current.children || []) as HTMLDivElement[];
            const currentChild = allChildren[previousPagekey];
            const newChild = allChildren[pageStatus.pageKey];
            let newHeight = 0;
            if (newChild) {
                newChild.style.display = "block";
                newHeight = newChild.offsetHeight;
                newChild.removeAttribute("style");
            }
            if (wrapperRef.current && contentRef.current) {
                wrapperRef.current.style.transition = "none";
                contentRef.current.style.transition = "none";
                setTimeout(() => {
                    if (wrapperRef.current && contentRef.current) {
                        wrapperRef.current.style.height = `${currentChild.offsetHeight}px`;
                        wrapperRef.current.style.overflow = "hidden";
                        contentRef.current.style.gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
                        contentRef.current.style.width = "200%";
                        if (pageStatus.direction === "prev") {
                            contentRef.current.style.transform = "translateX(-50%)";
                        }
                        allChildren.map((child) => {
                            if (child !== currentChild && child !== newChild) {
                                child.style.removeProperty("display");
                            } else {
                                child.style.display = "block";
                            }
                        });
                        setTimeout(() => {
                            if (wrapperRef.current && contentRef.current) {
                                wrapperRef.current.style.removeProperty("transition");
                                contentRef.current.style.removeProperty("transition");
                                setTimeout(() => {
                                    if (wrapperRef.current && contentRef.current) {
                                        wrapperRef.current.style.height = `${newHeight}px`;
                                        if (pageStatus.direction === "prev") {
                                            contentRef.current.style.transform = "translateX(0%)";
                                        } else {
                                            contentRef.current.style.transform = "translateX(-50%)";
                                        }
                                        setTimeout(() => {
                                            if (wrapperRef.current && contentRef.current) {
                                                wrapperRef.current.style.transition = "none";
                                                contentRef.current.style.transition = "none";
                                                contentRef.current.style.removeProperty("grid-template-columns");
                                                contentRef.current.style.removeProperty("width");
                                                contentRef.current.style.removeProperty("transform");
                                                wrapperRef.current.style.removeProperty("overflow");
                                                if (currentChild !== newChild) {
                                                    currentChild.style.removeProperty("display");
                                                }
                                                setTimeout(() => {
                                                    if (wrapperRef.current && contentRef.current) {
                                                        wrapperRef.current.style.removeProperty("height");
                                                        setTimeout(() => {
                                                            if (wrapperRef.current && contentRef.current) {
                                                                window.dispatchEvent(new Event("resize"));
                                                                wrapperRef.current.style.removeProperty("transition");
                                                                contentRef.current.style.removeProperty("transition");
                                                            }
                                                        }, 10);
                                                    }
                                                }, 10);
                                            }
                                        }, 310);
                                    }
                                }, 10);
                            }
                        }, 10);
                    }
                }, 10);
            }
        }
    }, [pageStatus]);

    return (
        <Wrapper className={className} ref={wrapperRef}>
            <Content ref={contentRef}>{children}</Content>
        </Wrapper>
    );
};

ContentTransition.Page = ContentTransitionPage;
ContentTransitionPage.displayName = "ContentTransitionPage";
